import { useState, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, Search, RefreshCw, FileText, DollarSign, Clock, AlertTriangle, Repeat } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Badge } from '@/app/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/app/components/ui/pagination';
import { useInvoices, useRecurringInvoiceConfigs, useCreateRecurringInvoiceConfig, useDeleteRecurringInvoiceConfig } from '@/hooks/use-projects';
import { useIndustry } from '@/hooks/use-industry';
import { useOrgCurrency } from '@/hooks/use-settings';
import { Label } from '@/app/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/app/components/ui/dialog';
import type { InvoiceStatus, Invoice } from '@/lib/types';
import { formatMoney } from '@/lib/currency';
import { formatDateOnly } from '@/lib/date-utils';

const statusColors: Record<InvoiceStatus, string> = {
  draft: 'bg-gray-100 text-gray-800',
  sent: 'bg-blue-100 text-blue-800',
  viewed: 'bg-indigo-100 text-indigo-800',
  paid: 'bg-green-100 text-green-800',
  partial: 'bg-yellow-100 text-yellow-800',
  overdue: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-600',
  void: 'bg-gray-100 text-gray-600',
};

const statusLabels: Record<InvoiceStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  viewed: 'Viewed',
  paid: 'Paid',
  partial: 'Partial',
  overdue: 'Overdue',
  cancelled: 'Cancelled',
  void: 'Void',
};

const PAGE_SIZE = 20;

function daysBetween(dateStr: string): number {
  const due = new Date(dateStr);
  const now = new Date();
  return Math.floor((now.getTime() - due.getTime()) / (1000 * 60 * 60 * 24));
}

interface AgingSummary {
  current: number;
  days30: number;
  days60: number;
  days90: number;
  totalOutstanding: number;
}

function computeAging(invoices: Invoice[]): AgingSummary {
  const summary: AgingSummary = { current: 0, days30: 0, days60: 0, days90: 0, totalOutstanding: 0 };
  for (const inv of invoices) {
    if (['paid', 'void', 'cancelled', 'draft'].includes(inv.status)) continue;
    const balance = Number(inv.balance_due);
    if (balance <= 0) continue;
    summary.totalOutstanding += balance;
    const overdueDays = daysBetween(inv.due_date);
    if (overdueDays <= 0) {
      summary.current += balance;
    } else if (overdueDays <= 30) {
      summary.days30 += balance;
    } else if (overdueDays <= 60) {
      summary.days60 += balance;
    } else {
      summary.days90 += balance;
    }
  }
  return summary;
}

export function InvoicesPage() {
  const currency = useOrgCurrency();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(searchParams.get('q') || '');
  const { labels } = useIndustry();

  const page = parseInt(searchParams.get('page') || '1', 10);
  const statusFilter = (searchParams.get('status') || '') as InvoiceStatus | '';

  const { data, isLoading, isError, refetch } = useInvoices({
    page,
    page_size: PAGE_SIZE,
    status: statusFilter || undefined,
  });

  // Fetch all invoices for aging (without status filter)
  const { data: allData } = useInvoices({ page_size: 1000 });

  const invoices = data?.results || [];
  const totalCount = data?.count || 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const aging = useMemo(() => computeAging(allData?.results || []), [allData?.results]);

  const updateSearchParams = useCallback(
    (updates: Record<string, string>) => {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      if (!updates.page) {
        newParams.set('page', '1');
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const handleSearch = useCallback(() => {
    updateSearchParams({ q: searchInput, page: '1' });
  }, [searchInput, updateSearchParams]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSearch();
      }
    },
    [handleSearch]
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Invoices</h1>
          <p className="text-gray-600 mt-1">
            Manage invoices and payments
            {totalCount > 0 && ` (${totalCount} total)`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button asChild>
            <Link to="/projects/invoices/new">
              <Plus className="h-4 w-4 mr-2" />
              New Invoice
            </Link>
          </Button>
        </div>
      </div>

      {/* Aging Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-gray-500" />
              <span className="text-xs text-gray-500 font-medium">Total Outstanding</span>
            </div>
            <p className="text-lg font-bold mt-1">{formatMoney(aging.totalOutstanding, currency)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-green-500" />
              <span className="text-xs text-gray-500 font-medium">Current</span>
            </div>
            <p className="text-lg font-bold mt-1 text-green-700">{formatMoney(aging.current, currency)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-yellow-500" />
              <span className="text-xs text-gray-500 font-medium">1-30 Days</span>
            </div>
            <p className="text-lg font-bold mt-1 text-yellow-700">{formatMoney(aging.days30, currency)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-orange-500" />
              <span className="text-xs text-gray-500 font-medium">31-60 Days</span>
            </div>
            <p className="text-lg font-bold mt-1 text-orange-700">{formatMoney(aging.days60, currency)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              <span className="text-xs text-gray-500 font-medium">60+ Days</span>
            </div>
            <p className="text-lg font-bold mt-1 text-red-700">{formatMoney(aging.days90, currency)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search invoices..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleKeyDown}
                className="pl-10"
              />
            </div>
            <Select
              value={statusFilter}
              onValueChange={(value) =>
                updateSearchParams({ status: value === 'all' ? '' : value })
              }
            >
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="partial">Partial</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="void">Void</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleSearch}>Search</Button>
          </div>
        </CardContent>
      </Card>

      {/* Invoice Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : isError ? (
            <div className="p-6 text-center text-red-600">
              Failed to load invoices. Please try again.
            </div>
          ) : invoices.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">No invoices found.</p>
              <Button asChild className="mt-4">
                <Link to="/projects/invoices/new">Create your first invoice</Link>
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>{labels.client}</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Balance Due</TableHead>
                  <TableHead>Due Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell className="font-mono text-sm">
                      <Link
                        to={`/projects/invoices/${invoice.id}`}
                        className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {invoice.invoice_number}
                      </Link>
                    </TableCell>
                    <TableCell className="text-gray-600">{invoice.client_name}</TableCell>
                    <TableCell className="text-gray-600">{invoice.project_name}</TableCell>
                    <TableCell>
                      <Badge className={statusColors[invoice.status]}>
                        {statusLabels[invoice.status]}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatMoney(invoice.total, currency)}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatMoney(invoice.balance_due, currency)}
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {formatDateOnly(invoice.due_date)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Recurring Invoice Configs */}
      <RecurringInvoiceSection />

      {totalPages > 1 && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => updateSearchParams({ page: String(Math.max(1, page - 1)) })}
                className={page <= 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }
              return (
                <PaginationItem key={pageNum}>
                  <PaginationLink
                    onClick={() => updateSearchParams({ page: String(pageNum) })}
                    isActive={page === pageNum}
                    className="cursor-pointer"
                  >
                    {pageNum}
                  </PaginationLink>
                </PaginationItem>
              );
            })}
            <PaginationItem>
              <PaginationNext
                onClick={() =>
                  updateSearchParams({ page: String(Math.min(totalPages, page + 1)) })
                }
                className={
                  page >= totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'
                }
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
}

function RecurringInvoiceSection() {
  const { data: configs } = useRecurringInvoiceConfigs();
  const createConfig = useCreateRecurringInvoiceConfig();
  const deleteConfig = useDeleteRecurringInvoiceConfig();
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    project: '',
    frequency: 'monthly' as 'weekly' | 'biweekly' | 'monthly',
    day_of_week: 0,
    day_of_month: 1,
    auto_include_time_entries: true,
    auto_include_expenses: false,
  });

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    await createConfig.mutateAsync({
      project: parseInt(form.project, 10),
      frequency: form.frequency,
      day_of_week: form.frequency === 'monthly' ? undefined : form.day_of_week,
      day_of_month: form.frequency === 'monthly' ? form.day_of_month : undefined,
      auto_include_time_entries: form.auto_include_time_entries,
      auto_include_expenses: form.auto_include_expenses,
      is_active: true,
    });
    setShowAdd(false);
  };

  const frequencyLabels: Record<string, string> = {
    weekly: 'Weekly',
    biweekly: 'Bi-weekly',
    monthly: 'Monthly',
  };

  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <Repeat className="h-4 w-4" />
          Recurring Invoices
        </CardTitle>
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogTrigger asChild>
            <Button size="sm" variant="outline">
              <Plus className="h-4 w-4 mr-1" />
              Set Up Recurring
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Set Up Recurring Invoice</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <Label>Project ID *</Label>
                <Input
                  type="number"
                  value={form.project}
                  onChange={(e) => setForm((p) => ({ ...p, project: e.target.value }))}
                  placeholder="Enter project ID"
                  required
                />
              </div>
              <div>
                <Label>Frequency *</Label>
                <Select
                  value={form.frequency}
                  onValueChange={(v) => setForm((p) => ({ ...p, frequency: v as 'weekly' | 'biweekly' | 'monthly' }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="biweekly">Bi-weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.frequency !== 'monthly' && (
                <div>
                  <Label>Day of Week</Label>
                  <Select
                    value={String(form.day_of_week)}
                    onValueChange={(v) => setForm((p) => ({ ...p, day_of_week: parseInt(v, 10) }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {dayNames.map((name, i) => (
                        <SelectItem key={i} value={String(i)}>{name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {form.frequency === 'monthly' && (
                <div>
                  <Label>Day of Month (1-28)</Label>
                  <Input
                    type="number"
                    min={1}
                    max={28}
                    value={form.day_of_month}
                    onChange={(e) => setForm((p) => ({ ...p, day_of_month: parseInt(e.target.value, 10) }))}
                  />
                </div>
              )}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="auto_time"
                  checked={form.auto_include_time_entries}
                  onChange={(e) => setForm((p) => ({ ...p, auto_include_time_entries: e.target.checked }))}
                />
                <Label htmlFor="auto_time">Auto-include time entries</Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="auto_expenses"
                  checked={form.auto_include_expenses}
                  onChange={(e) => setForm((p) => ({ ...p, auto_include_expenses: e.target.checked }))}
                />
                <Label htmlFor="auto_expenses">Auto-include expenses</Label>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createConfig.isPending}>
                  {createConfig.isPending ? 'Creating...' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {configs && configs.length > 0 ? (
          <div className="space-y-3">
            {configs.map((config) => (
              <div
                key={config.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium">Project #{config.project}</p>
                    <Badge variant={config.is_active ? 'default' : 'secondary'}>
                      {config.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                    <Badge variant="outline">
                      {frequencyLabels[config.frequency] ?? config.frequency}
                    </Badge>
                  </div>
                  <div className="text-xs text-gray-500 flex gap-4">
                    <span>Next: {config.next_generation}</span>
                    {config.last_generated && <span>Last: {config.last_generated}</span>}
                    {config.auto_include_time_entries && <span>+ Time Entries</span>}
                    {config.auto_include_expenses && <span>+ Expenses</span>}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-500 hover:text-red-700"
                  onClick={() => {
                    if (confirm('Delete this recurring invoice config?')) {
                      deleteConfig.mutate(config.id);
                    }
                  }}
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 text-center py-4">
            No recurring invoices configured. Set one up to auto-generate invoices on a schedule.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
